import { useEffect, useState, useCallback } from "react";
import DashboardShell from "@/components/DashboardShell";
import api, { formatApiError, downloadInvoicePdf, downloadPdf, fileUrl } from "@/lib/api";
import OrderMiniMap from "@/components/OrderMiniMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard, Package, Users, Tag, Truck, FileText, CreditCard, Settings as SettingsIcon,
  Plus, Trash2, Check, AlertTriangle, Download, Pencil, Repeat, Pause, Play, Mail, MessageSquare, QrCode, Scale, History, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const money = (n) => "£" + Number(n || 0).toFixed(2);
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "orders", label: "Orders", icon: Package },
  { key: "recurring", label: "Recurring", icon: Repeat },
  { key: "clients", label: "Clients", icon: Users },
  { key: "pricing", label: "Price list", icon: Tag },
  { key: "drivers", label: "Drivers & jobs", icon: Truck },
  { key: "invoices", label: "Invoices", icon: FileText },
  { key: "subscription", label: "Subscription", icon: CreditCard },
  { key: "account", label: "Settings", icon: SettingsIcon },
];
const STATUS_COLORS = {
  new: "bg-slate-100 text-slate-700", collected: "bg-blue-100 text-blue-700",
  processing: "bg-amber-100 text-amber-700", ready: "bg-teal-100 text-teal-700",
  out_for_delivery: "bg-indigo-100 text-indigo-700", completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};


function Pricing() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", price: "", unit: "item", category: "General" });
  const load = useCallback(() => api.get("/business/items").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name || !form.price) return toast.error("Name and price required");
    try { await api.post("/business/items", { ...form, price: Number(form.price) }); setForm({ name: "", price: "", unit: "item", category: "General" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/business/items/${id}`); load(); };

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold text-slate-900">Price list</h1>
      <div className="mt-5 flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="space-y-1.5"><Label>Service / item</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="item-name" placeholder="Bed linen (per kg)" /></div>
        <div className="space-y-1.5"><Label>Price (£)</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} data-testid="item-price" className="w-28" /></div>
        <div className="space-y-1.5"><Label>Unit</Label><Input value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} data-testid="item-unit" className="w-24" /></div>
        <div className="space-y-1.5"><Label>Category</Label><Input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} data-testid="item-category" className="w-36" /></div>
        <Button onClick={add} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="item-add-btn"><Plus className="mr-1 h-4 w-4" /> Add</Button>
      </div>
      <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="pricing-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400"><tr><th className="px-4 py-3">Item</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Price</th><th className="px-4 py-3">Unit</th><th className="px-4 py-3"></th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((it) => (
              <tr key={it.id} data-testid={`item-row-${it.id}`}>
                <td className="px-4 py-3 font-medium">{it.name}</td>
                <td className="px-4 py-3 text-slate-500">{it.category}</td>
                <td className="px-4 py-3">{money(it.price)}</td>
                <td className="px-4 py-3 text-slate-500">per {it.unit}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => remove(it.id)} className="text-slate-300 hover:text-red-500" data-testid={`item-delete-${it.id}`}><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-400">No items yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------------------- Drivers & jobs ---------------------------- */

export default Pricing;
